import React, { useRef, useState } from 'react';
import {
    Box, Paper, Typography, Button, Alert, AlertTitle, Stack, Divider, Chip,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, TextField,
    LinearProgress,
} from '@mui/material';
import CloudDownloadOutlinedIcon from '@mui/icons-material/CloudDownloadOutlined';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import KeyOutlinedIcon from '@mui/icons-material/KeyOutlined';
import api from '../services/api';
import { solicitarAutorizacao, consumirAutorizacao } from '../services/autorizacaoOperacao';

const OPERACAO_ENSAIO = 'BACKUP_ENSAIO';

/** Nome do arquivo que o servidor mandou; sem ele, um nome com a data de hoje. */
const nomeDoCabecalho = (headers) => {
    const disposicao = headers?.['content-disposition'] || '';
    const achado = /filename="?([^"]+)"?/.exec(disposicao);
    return achado ? achado[1] : `sigapsi_backup_${new Date().toISOString().slice(0, 10)}.zip`;
};

const mensagemDoErro = (e, padrao) =>
    e?.response?.data?.message || e?.response?.data?.erro || padrao;

/**
 * Backup do consultório: baixar o pacote e conferir um pacote já baixado.
 *
 * <b>Conferir é o ponto da tela.</b> Backup que ninguém testou é uma promessa:
 * o arquivo existe, abre, e só na hora da emergência se descobre que estava
 * truncado. O ensaio restaura o pacote num banco descartável do servidor e
 * mostra quantas tabelas e linhas voltaram — depois derruba o banco. O dado em
 * uso nunca é tocado.
 */
export default function Backup() {
    const entradaArquivo = useRef(null);

    const [baixando, setBaixando] = useState(false);
    const [arquivo, setArquivo] = useState(null);
    const [pedindoSenha, setPedindoSenha] = useState(false);
    const [senha, setSenha] = useState('');
    const [conferindo, setConferindo] = useState(false);
    const [laudo, setLaudo] = useState(null);
    const [erro, setErro] = useState(null);

    const baixar = async () => {
        setBaixando(true);
        setErro(null);
        try {
            const resposta = await api.get('/backup', { responseType: 'blob' });
            const url = window.URL.createObjectURL(new Blob([resposta.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', nomeDoCabecalho(resposta.headers));
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (e) {
            setErro(mensagemDoErro(e, 'Não foi possível gerar o backup.'));
        } finally {
            setBaixando(false);
        }
    };

    const escolher = (e) => {
        const f = e.target.files?.[0] || null;
        setArquivo(f);
        setLaudo(null);
        setErro(null);
    };

    const conferir = async () => {
        setPedindoSenha(false);
        setConferindo(true);
        setErro(null);
        setLaudo(null);
        try {
            await solicitarAutorizacao(OPERACAO_ENSAIO, senha);
            const token = consumirAutorizacao(OPERACAO_ENSAIO);

            // Corpo bruto, não multipart: o backup passa do teto de upload do
            // sistema, e subir esse teto afrouxaria todos os outros envios.
            const { data } = await api.post('/backup/ensaio', arquivo, {
                headers: {
                    'Content-Type': 'application/octet-stream',
                    'X-Autorizacao': token,
                },
            });
            setLaudo(data);
        } catch (e) {
            setErro(mensagemDoErro(e, 'Não foi possível conferir o pacote.'));
        } finally {
            setSenha('');
            setConferindo(false);
        }
    };

    return (
        <Box sx={{ p: { xs: 2, md: 3 } }}>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 0.5, color: 'secondary.main' }}>
                Backup
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Baixe uma cópia completa do consultório e confira, sem risco, se ela realmente volta.
            </Typography>

            {erro && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErro(null)}>{erro}</Alert>}

            <Paper variant="outlined" sx={{ p: 2.5, mb: 2 }}>
                <Stack direction="row" spacing={1.5} alignItems="flex-start">
                    <CloudDownloadOutlinedIcon color="primary" />
                    <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            Baixar backup agora
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                            Um .zip com o banco do consultório, os anexos e um manifesto com a
                            assinatura de cada arquivo. Guarde fora do servidor: backup no mesmo
                            disco do banco não sobrevive ao acidente que o justifica.
                        </Typography>
                        <Button
                            variant="contained"
                            disableElevation
                            startIcon={<CloudDownloadOutlinedIcon />}
                            loading={baixando}
                            onClick={baixar}
                        >
                            Baixar backup
                        </Button>
                        {baixando && (
                            <Box sx={{ mt: 1.5 }}>
                                <LinearProgress />
                                <Typography variant="caption" color="text.secondary">
                                    Montando o pacote. Com muitos anexos isso leva alguns minutos.
                                </Typography>
                            </Box>
                        )}
                    </Box>
                </Stack>
            </Paper>

            <Alert severity="warning" icon={<KeyOutlinedIcon />} sx={{ mb: 2 }}>
                <AlertTitle>A chave de criptografia não vai no pacote</AlertTitle>
                Os campos clínicos são gravados criptografados com a chave <code>AES_SECRET</code>,
                que fica fora do backup de propósito — chave junto do dado anula a criptografia.
                Sem ela, este pacote não recupera prontuário nenhum. Confira que você tem a chave
                guardada em outro lugar.
            </Alert>

            <Paper variant="outlined" sx={{ p: 2.5 }}>
                <Stack direction="row" spacing={1.5} alignItems="flex-start">
                    <FactCheckOutlinedIcon color="primary" />
                    <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            Conferir um backup
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                            O pacote é restaurado num banco descartável, conferido e o banco é
                            derrubado em seguida. Os dados em uso não são tocados.
                        </Typography>

                        <input
                            ref={entradaArquivo}
                            type="file"
                            accept=".zip"
                            onChange={escolher}
                            style={{ display: 'none' }}
                        />
                        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
                            <Button
                                variant="outlined"
                                startIcon={<UploadFileIcon />}
                                onClick={() => entradaArquivo.current?.click()}
                            >
                                Escolher arquivo
                            </Button>
                            <Button
                                variant="contained"
                                disableElevation
                                startIcon={<FactCheckOutlinedIcon />}
                                disabled={!arquivo}
                                loading={conferindo}
                                onClick={() => setPedindoSenha(true)}
                            >
                                Conferir
                            </Button>
                        </Stack>

                        {arquivo && (
                            <Chip
                                size="small"
                                sx={{ mt: 1.5 }}
                                label={`${arquivo.name} · ${(arquivo.size / 1024 / 1024).toFixed(1)} MB`}
                                onDelete={() => { setArquivo(null); setLaudo(null); }}
                            />
                        )}

                        {conferindo && (
                            <Box sx={{ mt: 1.5 }}>
                                <LinearProgress />
                                <Typography variant="caption" color="text.secondary">
                                    Restaurando e conferindo.
                                </Typography>
                            </Box>
                        )}
                    </Box>
                </Stack>

                {laudo && (
                    <>
                        <Divider sx={{ my: 2.5 }} />
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
                            Resultado do ensaio — consultório {laudo.schema}
                        </Typography>

                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(5, 1fr)' },
                                gap: 1.5, mb: 2,
                            }}
                        >
                            <Indicador rotulo="Tabelas" valor={laudo.tabelas} />
                            <Indicador rotulo="Linhas" valor={laudo.linhas?.toLocaleString('pt-BR')} />
                            <Indicador rotulo="Migrations" valor={laudo.migrations} />
                            <Indicador
                                rotulo="Anexos íntegros"
                                valor={`${laudo.anexosIntegros} de ${laudo.anexosNoPacote}`}
                                alerta={laudo.anexosIntegros !== laudo.anexosNoPacote}
                            />
                            <Indicador rotulo="Duração" valor={`${laudo.duracaoSegundos}s`} />
                        </Box>

                        {laudo.alertas?.length > 0 && (
                            <Alert severity="warning" sx={{ mb: 2 }}>
                                <AlertTitle>Passou, mas vale olhar</AlertTitle>
                                <ul style={{ margin: 0, paddingLeft: '1.2em' }}>
                                    {laudo.alertas.map((a, i) => <li key={i}>{a}</li>)}
                                </ul>
                            </Alert>
                        )}

                        {laudo.tabelas > 0 && laudo.linhas > 0 && !laudo.alertas?.length && (
                            <Alert severity="success" sx={{ mb: 2 }}>
                                O pacote restaurou por inteiro. O banco de ensaio
                                ({laudo.bancoEnsaio}) já foi derrubado.
                            </Alert>
                        )}

                        {laudo.maioresTabelas?.length > 0 && (
                            <TableContainer component={Paper} variant="outlined">
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Maiores tabelas restauradas</TableCell>
                                            <TableCell align="right">Linhas</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {laudo.maioresTabelas.map(t => (
                                            <TableRow key={t.nome}>
                                                <TableCell>{t.nome}</TableCell>
                                                <TableCell align="right">
                                                    {t.linhas?.toLocaleString('pt-BR')}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                    </>
                )}
            </Paper>

            <Dialog open={pedindoSenha} onClose={() => setPedindoSenha(false)} maxWidth="xs" fullWidth>
                <DialogTitle>Confirme sua senha</DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ mb: 2 }}>
                        O ensaio cria e derruba um banco no servidor. Confirme sua senha para
                        autorizar esta operação.
                    </DialogContentText>
                    <TextField
                        autoFocus
                        fullWidth
                        type="password"
                        label="Senha"
                        value={senha}
                        onChange={(e) => setSenha(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter' && senha) conferir(); }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => { setPedindoSenha(false); setSenha(''); }}>Cancelar</Button>
                    <Button variant="contained" disableElevation disabled={!senha} onClick={conferir}>
                        Conferir
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

function Indicador({ rotulo, valor, alerta = false }) {
    return (
        <Paper variant="outlined" sx={{ p: 1.5 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                {rotulo}
            </Typography>
            <Typography sx={{ fontWeight: 600, color: alerta ? '#B3261E' : 'secondary.main' }}>
                {valor}
            </Typography>
        </Paper>
    );
}
